import React, {useEffect, useState} from "react";
import {
    CircularProgress,
    Chip,
    Divider,
    Drawer,
    Dropdown,
    ListItemDecorator,
    Menu,
    MenuButton,
    MenuItem,
    Modal,
    ModalClose,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    Switch,
    Tab,
    TabList,
    TabPanel,
    Tabs,
    Textarea,
} from "@mui/joy";
import Box from "@mui/joy/Box";
import Input from "@mui/joy/Input";
import Option from "@mui/joy/Option";
import Typography from "@mui/joy/Typography";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import {Add, Block, DeleteForever, InfoOutlined, SolarPowerOutlined, WarningAmber} from "@mui/icons-material";
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import Tooltip from "@mui/joy/Tooltip";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import { toast, ToastContainer } from "react-toastify";
import GeneralApiService, { GetFranchiseVans } from "../../axios/Axios";
import ApiService, {getServicePrices, prepareBookingData} from "../../axios/Bookings-Axios";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import CustomersTopComponent from "./CustomersTopComponent";
import {INITIAL_SERVICES, Service as LocalService, ServiceState as LocalServiceState} from "../../domain/Services";
import ConfirmSendKlarnaInvoice from "./ConfirmSendKlarnaInvoice";
import DrawerWithStickyFooter from "../dashboard-components/DrawerWithStickyFooter";
import BookingActivityFlow from "../flows/BookingActivityFlow";
import CustomerMessagesTab from "../dashboard-pages/messages/CustomerMessagesTab";
import ConfirmMoveBookingModal from "./ConfirmMoveBookingModal";
import FranchiseeContactCard from "./FranchiseeContactCard";

const CANCELLATION_REASONS = [
    "Customer cancelled",
    "Employee not available",
    "Weather",
    "Customer not home",
    "Access issues",
    "Equipment failure",
    "Double booking",
    "Other"
];

interface Service extends LocalService {
}

interface ServiceState extends LocalServiceState {
}

interface RecurringBooking {
    created_at: string;
    start_date: Date | null;
    id: number;
    service: { id: number; name: string; };
    interval: string;
    frequency: string;
    active: boolean;
    customer: Customer;
    service_price: number | null;
}

interface Customer {
    advertisement_platform?: string;
    created_at: string;
    email: string;
    full_name: string;
    house_type: { name: string, id: number };
    id: number;
    notes: string;
    phone: string;
    postcode: string;
    street_address: string;
    unsubscribed?: boolean;
    franchise_postcode?: {
        franchise: { id: number; location: string; owner?: string; phone?: string; email?: string; };
        service_price_tier?: any;
    };
}

interface BookingReturn {
    id: number;
    return_date: string;
    days_to_complete: number;
    reason: string;
    van: { id: number; name: string } | null;
    created_at?: string;
    marked_for_delete?: boolean;
}

interface VanOption {
    id: number;
    name: string;
}

interface PaymentType {
    id?: number;
    name: string;
}

interface Booking {
    booked_in: string;
    created_at: string;
    customer: Customer;
    id: number;
    job_status: string;
    is_recurring: boolean;
    invoice_id: string;
    notes: string;
    paid: boolean;
    payment_type?: PaymentType | string | null;
    ask_for_review?: boolean;
    send_receipt?: boolean;
    send_before_after_photos?: boolean;
    guarantee_type?: string | null;
    services: Array<{ name: string } | string>;
    service_price: Array<{ service: { name: string } | string, price: number }>;
    recurring_booking: RecurringBooking[] | null;
    solar_panels: number;
    decking_si: number;
    patio_si: number;
    driveway_si: number;
    days_to_complete: number;
    returns?: BookingReturn[];
    ai_responses_enabled?: boolean;
    referral_badge_label?: string;
    referral_source_booking_id?: number | null;
    referral_referrer_name?: string | null;
    is_referral_reward?: boolean;
    is_referred_booking?: boolean;
}

function GroupedOtherBookings({loadedOtherBookings, otherBookings}) {
    const groupedBookings = otherBookings.reduce((acc, booking) => {
        const location = booking.customer?.franchise_postcode?.franchise?.location || "Unknown";
        if (!acc[location]) acc[location] = [];
        acc[location].push(booking);
        return acc;
    }, {} as Record<string, typeof otherBookings>);

    return (
        <Box sx={{flex: 1}}>
            {loadedOtherBookings ? (
                <>
                    <Typography level="title-md" sx={{mb: 1}}>
                        Other Bookings That Day:
                    </Typography>

                    {otherBookings.length === 0 ? (
                        <Typography>No bookings found</Typography>
                    ) : (
                        <Box sx={{borderRadius: "sm", maxHeight: "210px", pr: 1}}>
                            {Object.entries(groupedBookings).map(([location, bookings]) => (
                                <Box key={location}>
                                    <Typography level="title-sm" sx={{mb: 0.5}}>
                                        {location}
                                    </Typography>
                                    <List>
                                        {bookings.map((ob, index) => (
                                            <Tooltip
                                                key={index}
                                                title={formatServices(ob.services)}
                                                arrow
                                                placement="right"
                                            >
                                                <ListItem sx={{mb: "5px"}} variant="outlined">
                                                    {ob.customer?.full_name} - {formatDate(new Date(String(ob.booked_in)))}
                                                </ListItem>
                                            </Tooltip>
                                        ))}
                                    </List>
                                </Box>
                            ))}
                        </Box>
                    )}
                </>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                    }}
                >
                    <CircularProgress size="md"/>
                </Box>
            )}
        </Box>
    );
}


function formatDate(old_date: string | Date) {
    let date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: 'numeric', minute: 'numeric', second: 'numeric'
        });
    } else {
        return 'No Valid Date';
    }
}

const formatServices = (services: Array<{ name: string } | string>): string => {
    const serviceNames = services.map(service => typeof service === 'string' ? service : service.name);
    if (serviceNames.length === 0) return "No service selected.";
    if (serviceNames.length === 1) return serviceNames[0];
    if (serviceNames.length === 2) return `${serviceNames[0]} and ${serviceNames[1]}`;
    const lastService = serviceNames.pop();
    return `${serviceNames.join(', ')}, and ${lastService}`;
}

const getDateKey = (dateValue?: string | null): string | null => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function BookingsDrawer(
    {openState, bookingId, parentCallback, newBooking, newBookingValue}: {
        openState: boolean;
        bookingId: number;
        parentCallback: (updateInfo?: { id: number }) => void;
        newBooking: Booking | null;
        newBookingValue?: any;
    }
) {
    const [open, setOpen] = useState(openState);
    const [tab, setTab] = useState(0);
    const [loadedData, setLoadedData] = useState(false);
    const [loadedOtherBookings, setLoadedOtherBookings] = useState(false);
    const [otherBookings, setOtherBookings] = useState<Booking[]>([]);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [openSendKlarnaInvoice, setOpenSendKlarnaInvoice] = useState<boolean>(false);
    const [recurringBookings, setRecurringBookings] = useState<RecurringBooking[]>([]);
    
    // Cancellation modal state
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancellationReason, setCancellationReason] = useState<string | null>(null);
    const [cancellationDetails, setCancellationDetails] = useState<string>('');
    const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
    const [loadedAvailableDates, setLoadedAvailableDates] = useState(false);
    const [currentMonthView, setCurrentMonthView] = useState(new Date());
    const [isMonthLoading, setIsMonthLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [openMoveConfirmModal, setOpenMoveConfirmModal] = useState(false);
    const [initialBookedIn, setInitialBookedIn] = useState<string | null>(null);
    const [bookingReturns, setBookingReturns] = useState<BookingReturn[]>([]);
    const [availableVans, setAvailableVans] = useState<VanOption[]>([]);



    const initialCustomerState: Customer = {
        advertisement_platform: undefined, created_at: "", email: "", full_name: "",
        house_type: {name: 'Detached', id: 1}, id: 0, notes: "", phone: "",
        postcode: "", street_address: ""
    };
    const [customer, setCustomer] = useState<Customer>(initialCustomerState);

    const initialBookingState: Booking = {
        booked_in: '', created_at: new Date().toISOString(),
        customer: initialCustomerState, id: -1, job_status: "", is_recurring: false,
        invoice_id: '', notes: '', paid: false, payment_type: undefined,
        services: [], service_price: [], recurring_booking: null,
        solar_panels: 0, decking_si: 0, patio_si: 0, driveway_si: 0, days_to_complete: 1,
        returns: [],
    };
    const [booking, setBooking] = useState<Booking>(initialBookingState);
    const [services, setServices] = useState<ServiceState>({...INITIAL_SERVICES});

    const totalPrice = Object.values(services).reduce((sum, service) => sum + (service.selected ? Number(service.price) || 0 : 0), 0);

    const handleServiceToggle = (serviceKey: keyof ServiceState) => {
        setServices(prev => {
            const serviceToToggle = prev[serviceKey];
            if (!serviceToToggle) return prev;
            const isNowSelected = !serviceToToggle.selected;
            const updatedService = {
                ...serviceToToggle,
                selected: isNowSelected,
                price: isNowSelected ? serviceToToggle.defaultPrice : 0
            };
            const newState = {...prev, [serviceKey]: updatedService};
            if (!isNowSelected) {
                removeRecurringBooking(serviceToToggle.name);
                if (serviceKey === 'drivewayWash') setBooking(b => ({...b, driveway_si: 0}));
                else if (serviceKey === 'patioWash') setBooking(b => ({...b, patio_si: 0}));
                else if (serviceKey === 'deckingWash') setBooking(b => ({...b, decking_si: 0}));
            }
            return newState;
        });
    };
    useEffect(() => {
        const location = customer?.franchise_postcode?.franchise?.location;
        if (!location) {
            setAvailableDates(new Set());
            return;
        }

        setIsMonthLoading(true);

        const year = currentMonthView.getFullYear();
        const month = currentMonthView.getMonth();
        const startDate = new Date(year, month, 1).toISOString().split('T')[0];
        const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

        const excludeBookingId = booking?.id && booking.id > 0 ? booking.id : null;
        GeneralApiService.getFranchiseAvailableDates(
            location,
            startDate,
            endDate,
            booking.days_to_complete,
            excludeBookingId
        )
            .then(data => {
                setAvailableDates(new Set<string>(data.available_dates));
            })
            .catch(err => {
                console.error("Failed to fetch available dates:", err);
                setAvailableDates(new Set());
            })
            .finally(() => {
                setIsMonthLoading(false);
            });

    }, [customer?.franchise_postcode?.franchise?.location, currentMonthView, booking.days_to_complete]);

    const handleMonthChange = (date: Date) => {
        setCurrentMonthView(date);
    };
    const isDateAvailable = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        return availableDates.has(dateString);
    };

    useEffect(() => {
        if (services.solarPanelClean?.selected) {
            const panelCount = Number(booking.solar_panels) || 0;
            const calculatedPrice = panelCount > 0 ? (9 * panelCount) : 0;
            if (services.solarPanelClean.price !== calculatedPrice) {
                setServices(prev => ({...prev, solarPanelClean: {...prev.solarPanelClean, price: calculatedPrice}}));
            }
        }
    }, [booking.solar_panels, services.solarPanelClean?.selected]);
    const updateBookingNotes = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setBooking(prevBooking => ({...prevBooking, notes: e.target.value}));
    };
    const handlePriceChange = (serviceKey: keyof ServiceState, value: string) => {
        const price = parseFloat(value) || 0;
        setServices(prev => ({...prev, [serviceKey]: {...prev[serviceKey], price: price}}));
    };
    const addBookingReturn = () => {
        setBookingReturns(prev => ([
            ...prev,
            {
                id: -Math.floor(Math.random() * 1000000) - 1,
                return_date: '',
                days_to_complete: 1,
                reason: '',
                van: null,
            },
        ]));
    };
    const updateBookingReturnField = (returnId: number, field: 'return_date' | 'reason' | 'van' | 'days_to_complete', value: string | number | null) => {
        setBookingReturns(prev => prev.map((item) => {
            if (item.id !== returnId) return item;
            if (field === 'van') {
                const matchedVan = availableVans.find((van) => van.id === Number(value)) || null;
                return { ...item, van: matchedVan ? { id: matchedVan.id, name: matchedVan.name } : null };
            }
            if (field === 'days_to_complete') {
                const nextDays = Math.max(1, parseInt(String(value || 1), 10) || 1);
                return { ...item, days_to_complete: nextDays };
            }
            return { ...item, [field]: value as string };
        }));
    };
    const removeBookingReturn = (returnId: number) => {
        setBookingReturns(prev => prev.flatMap((item) => {
            if (item.id !== returnId) return [item];
            if (item.id > 0) {
                return [{ ...item, marked_for_delete: true }];
            }
            return [];
        }));
    };

    const syncBookingReturns = async (targetBookingId: number) => {
        const returnsToUpsert = bookingReturns.filter((item) => !item.marked_for_delete);
        const returnsToDelete = bookingReturns.filter((item) => item.marked_for_delete && item.id > 0);

        const missingFields = returnsToUpsert.find((item) => !item.return_date || !item.reason.trim() || !item.van?.id || !item.days_to_complete || item.days_to_complete < 1);
        if (missingFields) {
            throw new Error('Each return must include date, days to complete, reason, and van.');
        }

        await Promise.all(returnsToDelete.map((item) => ApiService.deleteBookingReturn(item.id)));

        for (const item of returnsToUpsert) {
            const payload = {
                return_date: item.return_date,
                days_to_complete: item.days_to_complete,
                reason: item.reason.trim(),
                van: item.van?.id,
            };
            if (item.id > 0) {
                await ApiService.updateBookingReturn(item.id, payload);
            } else {
                await ApiService.createBookingReturn(targetBookingId, payload);
            }
        }
    };

    function addRecurringBooking(serviceName: string, interval: string, frequency: string) {
        const newRecBooking: RecurringBooking = {
            created_at: new Date().toISOString(),
            start_date: null,
            id: 0,
            service: {id: 0, name: serviceName},
            interval: interval,
            frequency: frequency,
            active: true,
            customer: customer,
            service_price: null
        };
        setRecurringBookings(prev => prev.filter(rb => rb.service.name !== serviceName).concat(newRecBooking));
    }

    function removeRecurringBooking(serviceName: string) {
        setRecurringBookings(prev => prev.filter(rb => rb.service.name !== serviceName));
    }

    const handleAddRecurringBooking = (newValue: string | null, serviceName: string) => {
        if (newValue === 'one-time') removeRecurringBooking(serviceName); else if (newValue) {
            const [interval, frequency] = newValue.split(' ');
            addRecurringBooking(serviceName, interval, frequency);
        }
    };
    const handleDefaultValue = (serviceName: string) => {
        const rb = recurringBookings.find(b => b.service.name === serviceName);
        return rb ? `${rb.interval} ${rb.frequency}` : 'one-time';
    };
    const handleColor = (time: Date) => time.getHours() > 12 ? "" : "text-error";
    let handleDeleteCallBack = async (doDelete: boolean) => {
        setOpenDeleteModal(false);
        if (!doDelete) return;
        await ApiService.deleteBookingById(bookingId);
        window.location.reload(false); // Note: Delete still reloads, which is often desired behavior.
    };
    let handleSendKlaranaInvoiceCallback = async (doDelete: boolean) => {
        setOpenSendKlarnaInvoice(false);
    };
    
    const handleCancelModalClose = () => {
        setCancelModalOpen(false);
        setCancellationReason(null);
        setCancellationDetails('');
    };

    const handleConfirmCancellation = async () => {
        if (!bookingId || !cancellationReason || !cancellationDetails.trim()) {
            toast.error('Please select a reason and provide details before cancelling.', { containerId: "bookings-drawer", autoClose: 3000 });
            return;
        }

        const fullCancellationReason = `${cancellationReason}: ${cancellationDetails.trim()}`;
        
        try {
            await ApiService.employeeUpdateBooking({
                booking_id: bookingId,
                notes: booking.notes,
                status: 'Mark As Cancelled',
                cancellation_reason: fullCancellationReason,
            });
            toast.success(`Booking ${bookingId} cancelled successfully!`, { containerId: "bookings-drawer", autoClose: 1500 });
            handleCancelModalClose();
            setOpen(false);
            parentCallback({ id: bookingId });
        } catch (error) {
            console.error(`Error cancelling booking ${bookingId}:`, error);
            toast.error(`Failed to cancel booking ${bookingId}.`, { containerId: "bookings-drawer" });
        }
    };

    const isCancellationValid = cancellationReason && cancellationDetails.trim().length > 0;

    function closeModal() {
        setOpen(false);
        parentCallback();
    }

    const processSave = async (notifyCustomer: boolean) => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            const payload = prepareBookingData(booking, services, recurringBookings);
            if (booking.id > 0) {
                await ApiService.updateBooking(bookingId, payload, notifyCustomer);
                await syncBookingReturns(bookingId);
                toast.success("Booking saved successfully!", { containerId: "bookings-drawer" });
                setOpen(false);
                parentCallback({ id: bookingId });
            } else {
                const newBookingResponse = await ApiService.createBooking(payload);
                toast.success("New booking created successfully!", { containerId: "bookings-drawer" });
                setOpen(false);
                if (newBookingResponse && newBookingResponse.id) {
                    parentCallback({ id: newBookingResponse.id });
                } else {
                    parentCallback({ id: -1 }); // Signal a generic refetch
                }
            }
        } catch (error) {
            console.error("Failed to save booking:", error);
            toast.error("Failed to save booking. Please try again.", { containerId: "bookings-drawer" });
        } finally {
            setIsSaving(false);
        }
    };

    const hasValidBookedIn = !!booking.booked_in && !isNaN(new Date(String(booking.booked_in)).getTime());

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isSaving) return;

        if (!hasValidBookedIn) {
            toast.error("Please select a date on the calendar before saving.", { containerId: "bookings-drawer" });
            return;
        }

        const didBookedInDateChange =
            booking.id > 0 && getDateKey(booking.booked_in) !== getDateKey(initialBookedIn);

        if (didBookedInDateChange) {
            setOpenMoveConfirmModal(true);
            return;
        }

        await processSave(true);
    };

    const handleConfirmMoveOnSave = async (notifyCustomer: boolean) => {
        setOpenMoveConfirmModal(false);
        await processSave(notifyCustomer);
    };

    const handleCancelMoveOnSave = () => {
        setOpenMoveConfirmModal(false);
    };

    useEffect(() => {
        setBooking(prev => ({...prev, customer: customer}));
    }, [customer]);

    useEffect(() => {
        if (!open || bookingId <= 0) {
            setAvailableVans([]);
            return;
        }
        const franchiseId = customer?.franchise_postcode?.franchise?.id;
        if (!franchiseId) {
            setAvailableVans([]);
            return;
        }

        let mounted = true;
        const loadVans = async () => {
            try {
                const vansResp = await GetFranchiseVans(franchiseId);
                if (!mounted) return;
                const vans = (vansResp?.data || []).map((van: any) => ({
                    id: van.id,
                    name: van.name,
                }));
                setAvailableVans(vans);
            } catch (error) {
                console.error('Failed to load vans for booking returns:', error);
                if (mounted) setAvailableVans([]);
            }
        };
        loadVans();
        return () => {
            mounted = false;
        };
    }, [open, bookingId, customer?.franchise_postcode?.franchise?.id]);

    useEffect(() => {
        if (!open) {
            setOpenMoveConfirmModal(false);
            setIsSaving(false);
        }
    }, [open]);
    useEffect(() => {
        if (!open || !booking?.booked_in || !customer?.id || !customer.franchise_postcode?.franchise?.location) {
            setOtherBookings([]);
            setLoadedOtherBookings(true);
            return;
        }
        setLoadedOtherBookings(false);
        const fetchData = async () => {
            const bookedInDate = new Date(booking.booked_in);
            if (isNaN(bookedInDate.getTime())) {
                setOtherBookings([]);
                setLoadedOtherBookings(true);
                return;
            }
            const year = bookedInDate.getFullYear();
            const month = String(bookedInDate.getMonth() + 1).padStart(2, '0');
            const day = String(bookedInDate.getDate()).padStart(2, '0');
            const formattedDateForAPI = `${year}-${month}-${day}`;

            try {
                const resp = await ApiService.getBookings({page: 1, bookedIn: formattedDateForAPI});
                setOtherBookings(resp?.results || []);
            } catch (error) {
                console.error("Failed to fetch other bookings for day:", error);
                setOtherBookings([]);
            } finally {
                setLoadedOtherBookings(true);
            }
        };
        fetchData();
    }, [open, booking.booked_in, customer.id, customer.franchise_postcode?.franchise?.location]);
    useEffect(() => {
        if (!open) {
            setLoadedData(false);
            return;
        }
        setLoadedData(false);
        const fetchData = async () => {
            const getInitializedServices = (basePrices: any, franchisePrices: any, currentBookingData?: Booking): ServiceState => {
                const newServicesState: ServiceState = {...INITIAL_SERVICES};
                for (const key in newServicesState) {
                    const serviceKey = key as keyof ServiceState;
                    const serviceName = newServicesState[serviceKey].name;
                    const defaultPrice = franchisePrices[`${serviceKey}_price`] ?? basePrices[`${serviceKey}Price`] ?? newServicesState[serviceKey].defaultPrice;
                    newServicesState[serviceKey].defaultPrice = defaultPrice;
                    if (currentBookingData) {
                        const isSelected = currentBookingData.services.some(s => (typeof s === 'string' ? s === serviceName : s.name === serviceName));
                        newServicesState[serviceKey].selected = isSelected;
                        if (isSelected) {
                            const bookedPriceObj = currentBookingData.service_price?.find(item => (typeof item.service === 'string' ? item.service === serviceName : item.service.name === serviceName));
                            newServicesState[serviceKey].price = bookedPriceObj?.price ?? defaultPrice;
                        } else {
                            newServicesState[serviceKey].price = 0;
                        }
                    }
                }
                return newServicesState;
            };

            try {
                if (newBooking && newBooking.id === -1) {
                    const tempCustomer = {...initialCustomerState, ...newBooking.customer};
                    if (tempCustomer.notes == null) tempCustomer.notes = '';
                    setCustomer(tempCustomer);
                    const tempBooking = {...initialBookingState, ...newBooking, customer: tempCustomer};
                    setBooking(tempBooking);
                    setInitialBookedIn(tempBooking.booked_in || null);
                    setServices(newBookingValue ? newBookingValue : {...INITIAL_SERVICES});
                    setRecurringBookings([]);
                    setBookingReturns([]);
                } else if (bookingId > 0) {
                    const [recurringData, bookingDataFromAPI] = await Promise.all([ApiService.getRecurringBookingByBookingId(bookingId), ApiService.getBookingById(bookingId)]);
                    const currentCustomerForPrices = bookingDataFromAPI?.customer;
                    const houseTypeChar = currentCustomerForPrices?.house_type?.name?.charAt(0)?.toUpperCase() || 'D';
                    const houseTypeName = currentCustomerForPrices?.house_type?.name || 'Detached';
                    const servicePriceTiers = await getServicePrices(houseTypeChar, houseTypeName);
                    const basePrices = servicePriceTiers[0] || {};
                    const franchisePrices = currentCustomerForPrices?.franchise_postcode?.service_price_tier || {};
                    setRecurringBookings(recurringData || []);
                    if (bookingDataFromAPI) {
                        if (bookingDataFromAPI.customer.notes == null) bookingDataFromAPI.customer.notes = '';
                        setCustomer(bookingDataFromAPI.customer);
                        setBooking(bookingDataFromAPI);
                        setInitialBookedIn(bookingDataFromAPI.booked_in || null);
                        setBookingReturns((bookingDataFromAPI.returns || []).map((item: any) => ({
                            id: item.id,
                            return_date: item.return_date || '',
                            days_to_complete: Math.max(1, Number(item.days_to_complete || 1)),
                            reason: item.reason || '',
                            van: item.van ? { id: item.van.id, name: item.van.name } : null,
                            created_at: item.created_at,
                            marked_for_delete: false,
                        })));
                        setServices(getInitializedServices(basePrices, franchisePrices, bookingDataFromAPI));
                    }
                } else {
                    setCustomer(initialCustomerState);
                    setBooking(initialBookingState);
                    setInitialBookedIn(null);
                    setServices({...INITIAL_SERVICES});
                    setRecurringBookings([]);
                    setBookingReturns([]);
                }
            } catch (error) {
                console.error("Error fetching booking details:", error);
            } finally {
                setLoadedData(true);
            }
        };
        fetchData();
    }, [open, bookingId, newBooking, newBookingValue]);
    const renderServiceRow = (serviceKey: keyof ServiceState) => {
        const service = services[serviceKey];
        if (!service) return null;
        return (<ListItem key={String(serviceKey)} variant="outlined" sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            width: '100%',
            boxShadow: 'sm',
            mb: 1,
            px: 2,
            py: 1.5,
            borderRadius: 'md',
        }}> <Box sx={{display: 'flex', alignItems: 'center', flex: 1, gap: 1.5}}>
            <ListItemDecorator>{service.icon}</ListItemDecorator> <Typography
            level="body-md">{service.label}</Typography> </Box> <Input startDecorator="£" type="number"
                                                                       value={service.price.toString()}
                                                                       onChange={(e) => handlePriceChange(serviceKey, e.target.value)}
                                                                       slotProps={{input: {min: 0, step: '0.01'}}}
                                                                       sx={{maxWidth: 200}}/> <IconButton color="danger"
                                                                                                          onClick={() => handleServiceToggle(serviceKey)}>
            <DeleteForever/> </IconButton> </ListItem>);
    };
    const renderRecurringBookingSelect = (serviceKey: keyof ServiceState) => {
        const service = services[serviceKey];
        if (!service || !service.selected) return null;
        const allOptions: Record<string, Array<{ value: string, label: string }>> = {
            gutterClean: [{
                value: "one-time",
                label: "One Time Booking"
            }, {value: "4 week", label: "Every 4 Weeks"}, {value: "6 week", label: "Every 6 Weeks"}, {
                value: "8 week",
                label: "Every 8 Weeks"
            }, {value: "12 week", label: "Every 12 Weeks"}],
            conservatoryGutterClean: [{value: "one-time", label: "One Time Booking"}, {
                value: "4 week",
                label: "Every 4 Weeks"
            }, {value: "6 week", label: "Every 6 Weeks"}, {value: "8 week", label: "Every 8 Weeks"}, {
                value: "12 week",
                label: "Every 12 Weeks"
            }],
            default: [{value: "one-time", label: "One Time Booking"}, {
                value: "3 month",
                label: "Every 3 Months"
            }, {value: "4 month", label: "Every 4 Months"}, {
                value: "6 month",
                label: "Every 6 Months"
            }, {value: "12 month", label: "Every 12 Months"}]
        };
        const serviceOptions = allOptions[String(serviceKey) as keyof typeof allOptions] || allOptions.default;
        const currentValue = handleDefaultValue(service.name) || 'one-time';
        return (<React.Fragment key={String(serviceKey) + "-recurring"}> <Typography
            sx={{mb: '5px', mt: '5px', width: '100%'}}> {service.label} </Typography> <Select value={currentValue}
                                                                                              onChange={(e, newValue) => handleAddRecurringBooking(newValue, service.name)}> {serviceOptions.map(option => (
            <Option key={option.value} value={option.value}> {option.label} </Option>))} </Select> </React.Fragment>);
    };
    const showSolarPanelInput = services.solarPanelClean?.selected;
    const showPressureWashingSection = services.drivewayWash?.selected || services.patioWash?.selected || services.deckingWash?.selected;
    const serviceKeys = Object.keys(services) as Array<keyof ServiceState>;
    const selectedServiceKeys = serviceKeys.filter(key => services[key]?.selected);
    const unselectedServiceKeys = serviceKeys.filter(key => !services[key]?.selected);
    const bookingServiceNames = booking.services.map(service => typeof service === 'string' ? service : service.name);
    const isCompletedBooking = booking.job_status === "Mark As Complete";
    const hasRoofCleanService = bookingServiceNames.some(name => name.toLowerCase() === "roof clean");
    const hasCertificateService = bookingServiceNames.some(name => ["Gutter Clean", "Facias & Soffits and Outside of Gutters"].includes(name) || name.toLowerCase() === "roof clean");
    const hasCustomerEmail = Boolean(customer.email);
    const hasCustomerPhone = Boolean(customer.phone);
    const isCustomerOptedOut = Boolean(customer.unsubscribed);
    const paymentTypeName = typeof booking.payment_type === 'string' ? booking.payment_type : booking.payment_type?.name;
    const isInvoicePayment = paymentTypeName === "Invoice";
    const visibleBookingReturns = bookingReturns.filter((item) => !item.marked_for_delete);
    const [runningCompletedAction, setRunningCompletedAction] = useState<string | null>(null);

    const completedBookingActions = [
        {
            id: "completion_sms",
            label: booking.ask_for_review ? "Send completion SMS + review request" : "Send completion SMS",
            description: "Re-sends the SMS that goes out when a booking is completed.",
            destination: customer.phone,
            destinationLabel: "phone",
            disabledReason: !isCompletedBooking ? "Only available for completed bookings." : isCustomerOptedOut ? "Customer is opted out." : !hasCustomerPhone ? "Customer has no phone number." : null,
        },
        {
            id: "gutter_certificate",
            label: "Send gutter certificate email",
            description: "Queues the customer certificate email for gutter or roof clean bookings.",
            destination: customer.email,
            destinationLabel: "email",
            disabledReason: !isCompletedBooking ? "Only available for completed bookings." : isCustomerOptedOut ? "Customer is opted out." : !hasCertificateService ? "Booking does not include a certificate service." : null,
        },
        {
            id: "receipt_email",
            label: "Send receipt email",
            description: "Queues the receipt email normally sent after completion.",
            destination: customer.email,
            destinationLabel: "email",
            disabledReason: !isCompletedBooking ? "Only available for completed bookings." : isCustomerOptedOut ? "Customer is opted out." : !hasCustomerEmail ? "Customer has no email address." : isInvoicePayment ? "Invoice payment bookings do not receive receipt emails." : null,
        },
        {
            id: "roof_guarantee_2_year",
            label: "Send 2-year roof guarantee",
            description: "Queues the biocide treatment roof clean guarantee email.",
            destination: customer.email,
            destinationLabel: "email",
            disabledReason: !isCompletedBooking ? "Only available for completed bookings." : isCustomerOptedOut ? "Customer is opted out." : !hasRoofCleanService ? "Booking does not include Roof Clean." : !hasCustomerEmail ? "Customer has no email address." : null,
        },
        {
            id: "roof_guarantee_10_year",
            label: "Send 10-year roof guarantee",
            description: "Queues the biocide treatment and sealant roof clean guarantee email.",
            destination: customer.email,
            destinationLabel: "email",
            disabledReason: !isCompletedBooking ? "Only available for completed bookings." : isCustomerOptedOut ? "Customer is opted out." : !hasRoofCleanService ? "Booking does not include Roof Clean." : !hasCustomerEmail ? "Customer has no email address." : null,
        },
        {
            id: "before_after_photos",
            label: "Send before/after photos email",
            description: "Re-sends the before and after photos email, if photos have been uploaded.",
            destination: customer.email,
            destinationLabel: "email",
            disabledReason: !isCompletedBooking ? "Only available for completed bookings." : isCustomerOptedOut ? "Customer is opted out." : !hasCustomerEmail ? "Customer has no email address." : null,
        },
    ];

    const handleRunCompletedAction = async (actionId: string) => {
        if (!bookingId || runningCompletedAction) return;
        const action = completedBookingActions.find((item) => item.id === actionId);
        setRunningCompletedAction(actionId);
        try {
            const response = await ApiService.runCompletedBookingAction(bookingId, actionId);
            const destinationText = action?.destination ? ` to ${action.destinationLabel} ${action.destination}` : "";
            toast.success(`${response?.message || "Booking action queued."}${destinationText}`, { containerId: "bookings-drawer", autoClose: 2500 });
        } catch (error: any) {
            const message = error?.response?.data?.error || "Failed to run booking action.";
            toast.error(message, { containerId: "bookings-drawer", autoClose: 3500 });
        } finally {
            setRunningCompletedAction(null);
        }
    };

    const renderFooter = () => {
        if (!loadedData) return <Box sx={{mr: 'auto'}}/>;
        const isExistingBooking = bookingId > 0;
        return (
            <>
                {/* Mobile layout - stacked */}
                <Box sx={{ 
                    display: { xs: 'flex', sm: 'none' }, 
                    flexDirection: 'column',
                    gap: 1,
                    width: '100%',
                }}>
                    {isExistingBooking && (
                        <>
                            <Button color="danger" startDecorator={<DeleteForever/>}
                                    onClick={() => setOpenDeleteModal(true)} fullWidth>Delete</Button>
                            <Button color="warning" variant="soft" startDecorator={<Block/>}
                                    onClick={() => setCancelModalOpen(true)} fullWidth>Cancel Job</Button>
                        </>
                    )}
                    {isExistingBooking ? ((!booking.invoice_id) ? (
                        <Button onClick={() => setOpenSendKlarnaInvoice(true)} fullWidth>Send Klarna Invoice</Button>) : (
                        <Button disabled fullWidth>Send Klarna Invoice</Button>)) : (
                        <Button disabled fullWidth>Send Klarna Invoice</Button>)}
                    <Button type="submit" form="booking-drawer-form" fullWidth disabled={isSaving || !hasValidBookedIn}>
                        {isSaving ? 'Saving...' : !hasValidBookedIn ? 'Select a Date' : (isExistingBooking ? 'Save Booking' : 'Create New Booking')}
                    </Button>
                </Box>
                
                {/* Desktop layout - horizontal */}
                <Box sx={{ 
                    display: { xs: 'none', sm: 'flex' }, 
                    gap: 1,
                    width: '100%',
                    alignItems: 'center',
                }}>
                    {isExistingBooking ? (
                        <Box sx={{ mr: 'auto', display: 'flex', gap: 1 }}>
                            <Button color="danger" startDecorator={<DeleteForever/>}
                                    onClick={() => setOpenDeleteModal(true)}>Delete</Button>
                            <Button color="warning" variant="soft" startDecorator={<Block/>}
                                    onClick={() => setCancelModalOpen(true)}>Cancel Job</Button>
                        </Box>
                    ) : (
                        <Box sx={{ mr: 'auto' }}/>
                    )}
                    {isExistingBooking ? ((!booking.invoice_id) ? (
                        <Button onClick={() => setOpenSendKlarnaInvoice(true)}>Send Klarna Invoice</Button>) : (
                        <Tooltip title="An invoice has already been sent to this customer." color="danger" placement="top"
                                 variant="solid"><Box><Button disabled>Send Klarna Invoice</Button></Box></Tooltip>)) : (
                        <Tooltip title="You need to create this booking before sending an invoice." color="danger"
                                 placement="top" variant="solid"><Box><Button disabled>Send Klarna Invoice</Button></Box></Tooltip>)}
                    <Button type="submit" form="booking-drawer-form" sx={{ ml: 1 }} disabled={isSaving || !hasValidBookedIn}>
                        {isSaving ? 'Saving...' : !hasValidBookedIn ? 'Select a Date' : (isExistingBooking ? 'Save Booking' : 'Create New Booking')}
                    </Button>
                </Box>
            </>
        );
    };

    return (
        <>
            <ToastContainer containerId={"bookings-drawer"} position="top-right" autoClose={3000} theme="dark" newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
            <Drawer open={open} onClose={closeModal} anchor="right" slotProps={{
                backdrop: {sx: {backdropFilter: 'none', backgroundColor: 'rgb(108 108 108 / 60%)'}},
                content: {sx: {
                    top: 'var(--Header-height)', 
                    height: 'calc(100vh - var(--Header-height))'
                }}
            }} sx={{'--Drawer-horizontalSize': { xs: '100vw', sm: '85vw', md: '50vw' }}}>
                <DrawerWithStickyFooter footer={renderFooter()}>
                    <ModalClose sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }} />
                    <form onSubmit={handleSave} id="booking-drawer-form" style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, flex: 1}}>
                        <Tabs sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', '& .MuiTabPanel-root': { flex: 1, overflowY: 'auto', minHeight: 0 }}} aria-label="Booking Modal Tabs" value={tab}
                              onChange={(event, newValue) => setTab(newValue as number)}>
                            <TabList sx={{ flexShrink: 0 }}>
                                <Tab>Booking Details</Tab>
                                <Tab disabled={bookingId <= 0}>Action Log</Tab>
                                <Tab disabled={bookingId <= 0}>Messages</Tab>
                                <Tab disabled={bookingId <= 0}>Actions</Tab>
                            </TabList>
                            <TabPanel value={0}>
                                {!loadedData ? (<Box sx={{
                                    display: 'flex',
                                    minHeight: '300px',
                                    gap: 2,
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}><CircularProgress size="lg"/></Box>) : (
                                    <Box>
                                        <FranchiseeContactCard franchise={customer.franchise_postcode?.franchise} />
                                        {booking.referral_badge_label && (
                                            <Box sx={{ mb: '10px', p: 1.5, border: '1px solid', borderColor: 'primary.outlinedBorder', borderRadius: 'sm', bgcolor: 'primary.softBg' }}>
                                                <Chip color="primary" variant="solid" size="sm" sx={{ mb: 0.75 }}>
                                                    {booking.referral_badge_label}
                                                </Chip>
                                                <Typography level="body-sm">
                                                    {booking.is_referral_reward
                                                        ? `This is a free gutter clean earned from referred roof booking #${booking.referral_source_booking_id || 'unknown'}.`
                                                        : `This roof booking came through a referral link${booking.referral_referrer_name ? ` from ${booking.referral_referrer_name}` : ''}.`}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ mb: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                                            <Box>
                                                <Typography level="title-sm">Ronald AI auto-replies</Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    When on, Ronald will automatically reply to customer SMS on this booking.
                                                </Typography>
                                            </Box>
                                            <Switch
                                                size="lg"
                                                checked={booking.ai_responses_enabled !== false}
                                                onChange={(e) => setBooking(prev => ({ ...prev, ai_responses_enabled: e.target.checked }))}
                                            />
                                        </Box>
                                        <CustomersTopComponent customer={customer} setCustomer={setCustomer}/>
                                        <Box sx={{mt: '10px'}}><Typography variant="soft"
                                                                           sx={{mb: '5px', width: '100%'}}>Booking
                                            Notes</Typography><Textarea name="notes" value={booking.notes || ''}
                                                                        onChange={updateBookingNotes} variant="outlined"
                                                                        minRows={3}/></Box>
                                        {showSolarPanelInput && (<Box sx={{mt: '10px'}}><Typography variant="soft" sx={{
                                            mb: '5px',
                                            width: '100%'
                                        }}> Solar Panels </Typography><Input startDecorator={<SolarPowerOutlined/>}
                                                                             type="number"
                                                                             value={booking.solar_panels || 0}
                                                                             onChange={(e) => {
                                                                                 const val = parseInt(e.target.value, 10);
                                                                                 setBooking(prev => ({
                                                                                     ...prev,
                                                                                     solar_panels: isNaN(val) || val < 0 ? 0 : val
                                                                                 }));
                                                                             }}
                                                                             sx={{width: '100%', background: 'white'}}
                                                                             placeholder="Number of Solar Panels"
                                                                             slotProps={{input: {min: 0}}}/></Box>)}
                                        {showPressureWashingSection && (
                                            <Box sx={{mt: '10px'}}><Typography variant="soft"
                                                                               sx={{mb: '5px', width: '100%'}}> Pressure
                                                Washing Details
                                                (sqm²) </Typography>{services.drivewayWash?.selected && (
                                                <Box sx={{mb: 1}}><Typography sx={{mb: 0.5}}>Driveway
                                                    sqm²</Typography><Input type="number"
                                                                            value={booking.driveway_si || 0}
                                                                            onChange={(e) => {
                                                                                const v = parseFloat(e.target.value);
                                                                                setBooking(p => ({
                                                                                    ...p,
                                                                                    driveway_si: isNaN(v) || v < 0 ? 0 : v
                                                                                }));
                                                                            }} sx={{width: '100%', background: 'white'}}
                                                                            placeholder="Driveway area"
                                                                            endAdornment="sqm²"
                                                                            slotProps={{input: {min: 0, step: "0.1"}}}/></Box>)}{services.patioWash?.selected && (
                                                <Box sx={{mb: 1}}><Typography sx={{mb: 0.5}}>Patio
                                                    sqm²</Typography><Input type="number" value={booking.patio_si || 0}
                                                                            onChange={(e) => {
                                                                                const v = parseFloat(e.target.value);
                                                                                setBooking(p => ({
                                                                                    ...p,
                                                                                    patio_si: isNaN(v) || v < 0 ? 0 : v
                                                                                }));
                                                                            }} sx={{width: '100%', background: 'white'}}
                                                                            placeholder="Patio area" endAdornment="sqm²"
                                                                            slotProps={{input: {min: 0, step: "0.1"}}}/></Box>)}{services.deckingWash?.selected && (
                                                <Box sx={{mb: 1}}><Typography sx={{mb: 0.5}}>Decking
                                                    sqm²</Typography><Input type="number"
                                                                            value={booking.decking_si || 0}
                                                                            onChange={(e) => {
                                                                                const v = parseFloat(e.target.value);
                                                                                setBooking(p => ({
                                                                                    ...p,
                                                                                    decking_si: isNaN(v) || v < 0 ? 0 : v
                                                                                }));
                                                                            }} sx={{width: '100%', background: 'white'}}
                                                                            placeholder="Decking area"
                                                                            endAdornment="sqm²"
                                                                            slotProps={{input: {min: 0, step: "0.1"}}}/></Box>)}
                                            </Box>)}
                                        <Box sx={{mt: '10px'}}>
                                            <Box sx={{display: 'flex', flexDirection: 'column'}}><Typography
                                                variant="soft">Selected Services</Typography></Box>
                                            {selectedServiceKeys.length > 0 ? (<Box
                                                sx={{display: 'flex', flexDirection: 'column', width: '100%'}}><List
                                                sx={{width: '100%'}}>{selectedServiceKeys.map((serviceKey) => renderServiceRow(serviceKey))}</List></Box>) : (
                                                <Box sx={{
                                                    display: 'flex',
                                                    mb: '10px',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    p: 3,
                                                    border: '1px dashed',
                                                    borderColor: 'divider',
                                                    borderRadius: 'sm',
                                                    mt: 1
                                                }}><Typography level="body-sm">No services selected. Click "Add Service"
                                                    to begin.</Typography></Box>)}
                                        </Box>
                                        <Box sx={{width: '100%', display: 'flex'}}><Dropdown
                                            sx={{width: '100%'}}><MenuButton sx={{width: '100%'}} size="sm"
                                                                             color="primary" startDecorator={<Add/>}>Add
                                            Service</MenuButton><Menu
                                            sx={{zIndex: 9001}}>{unselectedServiceKeys.map((key) => {
                                            const service = services[key];
                                            if (!service) return null;
                                            return (<MenuItem key={key}
                                                              onClick={() => handleServiceToggle(key)}><ListItemDecorator>{service.icon}</ListItemDecorator>{service.label}
                                            </MenuItem>);
                                        })}{unselectedServiceKeys.length === 0 && (
                                            <MenuItem disabled>All services have been
                                                added</MenuItem>)}</Menu></Dropdown></Box>
                                        <Box sx={{
                                            width: '100%',
                                            mb: '5px',
                                            display: 'flex',
                                            justifyContent: 'flex-end'
                                        }}><Typography level="title-md" sx={{mt: 1}}>Total Price:
                                            £{totalPrice.toFixed(2)}</Typography>
                                            {booking.is_referral_reward && <Chip color="primary" sx={{ ml: 1 }}>Free referral reward</Chip>}
                                        </Box>
                                        {Object.values(services).some(s => s.selected) && (
                                            <Box sx={{marginTop: '10px'}}><Typography startDecorator={<Tooltip
                                                title="Recurring Bookings will be created a year in advance"><InfoOutlined/></Tooltip>}
                                                                                      variant="soft" sx={{
                                                marginBottom: '5px',
                                                width: '100%'
                                            }}>Recurring
                                                Booking</Typography>{Object.keys(services).filter(key => services[key].selected).map(key => renderRecurringBookingSelect(key as keyof ServiceState))}
                                            </Box>)}
                                        <Box sx={{mt: '10px'}}>
                                            <Typography variant="soft" sx={{mb: '5px', width: '100%'}}> Booking
                                                Time </Typography>

                                            <Box sx={{display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2}}>
                                                <Box sx={{minWidth: {sm: '250px'}}}>
                                                    <Box sx={{ position: 'relative', minHeight: 250 }}>
                                                        <DatePicker
                                                            key={currentMonthView.toISOString()}
                                                            inline
                                                            showDateSelect
                                                            selected={booking?.booked_in && !isNaN(new Date(String(booking.booked_in)).getTime()) ? new Date(String(booking.booked_in)) : null}
                                                            filterDate={isDateAvailable}
                                                            onChange={(date: Date | null) => {
                                                                if (date) {
                                                                    const nextDateKey = getDateKey(date);
                                                                    if (!nextDateKey || nextDateKey === getDateKey(booking.booked_in)) return;
                                                                    setBooking(prev => ({...prev, booked_in: nextDateKey}));
                                                                    setCurrentMonthView(date);
                                                                }
                                                            }}
                                                            onMonthChange={handleMonthChange}
                                                            openToDate={currentMonthView}
                                                            timeClassName={handleColor}
                                                        />

                                                        {isMonthLoading && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                                borderRadius: 'md',
                                                                zIndex: 1,
                                                            }}>
                                                                <CircularProgress size="md" />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <Typography  sx={{
                                                        mb: '5px',
                                                        width: '100%'
                                                    }}> Days To Complete </Typography>
                                                    <Input type="number"
                                                           value={booking.days_to_complete || 1}
                                                           onChange={(e) => {
                                                               const v = parseFloat(e.target.value);
                                                               const next = isNaN(v) || v < 0 ? 0 : v;
                                                               setBooking(p => {
                                                                   if (p.days_to_complete === next) return p;
                                                                   return {
                                                                       ...p,
                                                                       days_to_complete: next,
                                                                       booked_in: '',
                                                                   };
                                                               });
                                                           }} sx={{width: '100%', background: 'white'}}
                                                           placeholder="Days To Complete"
                                                           slotProps={{input: {min: 1, step: "1"}}}/>
                                                </Box>
                                            </Box>
                                        </Box>
                                        {bookingId > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="soft" sx={{ width: '100%', mb: 1 }}>
                                                    Returns
                                                </Typography>
                                                {visibleBookingReturns.length === 0 ? (
                                                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                                        No returns added for this booking.
                                                    </Typography>
                                                ) : (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        {visibleBookingReturns.map((bookingReturn) => (
                                                            <Box
                                                                key={bookingReturn.id}
                                                                sx={{
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    borderRadius: 'sm',
                                                                    p: 1.25,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' } }}>
                                                                    <Box sx={{ width: '100%' }}>
                                                                        <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
                                                                            Return date
                                                                        </Typography>
                                                                        <Input
                                                                            type="date"
                                                                            value={bookingReturn.return_date || ''}
                                                                            onChange={(e) => updateBookingReturnField(bookingReturn.id, 'return_date', e.target.value)}
                                                                            sx={{ width: '100%' }}
                                                                        />
                                                                    </Box>
                                                                    <Box sx={{ width: '100%' }}>
                                                                        <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
                                                                            Days to complete
                                                                        </Typography>
                                                                        <Input
                                                                            type="number"
                                                                            value={bookingReturn.days_to_complete || 1}
                                                                            onChange={(e) => updateBookingReturnField(bookingReturn.id, 'days_to_complete', e.target.value)}
                                                                            sx={{ width: '100%' }}
                                                                            slotProps={{ input: { min: 1, step: 1 } }}
                                                                            placeholder="Days"
                                                                        />
                                                                    </Box>
                                                                    <Box sx={{ width: '100%' }}>
                                                                        <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
                                                                            Assigned van
                                                                        </Typography>
                                                                        <Select
                                                                            placeholder="Select van"
                                                                            value={bookingReturn.van?.id ?? null}
                                                                            onChange={(_event, newValue) => updateBookingReturnField(bookingReturn.id, 'van', newValue)}
                                                                            sx={{ width: '100%' }}
                                                                        >
                                                                            {availableVans.map((van) => (
                                                                                <Option key={van.id} value={van.id}>
                                                                                    {van.name}
                                                                                </Option>
                                                                            ))}
                                                                        </Select>
                                                                    </Box>
                                                                </Box>
                                                                <Box sx={{ width: '100%' }}>
                                                                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
                                                                        Reason shown on calendar
                                                                    </Typography>
                                                                    <Input
                                                                        placeholder="e.g. Remove scaffolding"
                                                                        value={bookingReturn.reason}
                                                                        onChange={(e) => updateBookingReturnField(bookingReturn.id, 'reason', e.target.value)}
                                                                        sx={{ width: '100%' }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="soft"
                                                                        color="danger"
                                                                        startDecorator={<DeleteForever />}
                                                                        onClick={() => removeBookingReturn(bookingReturn.id)}
                                                                    >
                                                                        Delete Return
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                                <Box sx={{ width: '100%', display: 'flex', mt: 1 }}>
                                                    <Button
                                                        size="sm"
                                                        color="primary"
                                                        startDecorator={<Add />}
                                                        onClick={addBookingReturn}
                                                        sx={{ width: '100%' }}
                                                    >
                                                        Add Return
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </TabPanel>
                            <TabPanel value={1}>{bookingId > 0 &&
                                <BookingActivityFlow booking_id={bookingId}/>}</TabPanel>
                            <TabPanel value={2} style={{ height: '100%' }}>
                                {bookingId > 0 && booking.customer && <CustomerMessagesTab customerId={booking.customer.id} />}
                            </TabPanel>
                            <TabPanel value={3}>
                                {bookingId > 0 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box>
                                            <Typography level="title-md">Completed Booking Actions</Typography>
                                            <Typography level="body-sm" color="neutral">
                                                Re-run customer communications that normally happen when this booking is marked complete.
                                            </Typography>
                                        </Box>
                                        {completedBookingActions.map((action) => {
                                            const isRunning = runningCompletedAction === action.id;
                                            const isDisabled = Boolean(action.disabledReason) || Boolean(runningCompletedAction);
                                            const button = (
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    loading={isRunning}
                                                    disabled={isDisabled}
                                                    onClick={() => handleRunCompletedAction(action.id)}
                                                    sx={{ justifyContent: 'flex-start' }}
                                                >
                                                    {action.label}
                                                </Button>
                                            );

                                            return (
                                                <Box
                                                    key={action.id}
                                                    sx={{
                                                        p: 1.5,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 'sm',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 0.75,
                                                    }}
                                                >
                                                    {action.disabledReason ? (
                                                        <Tooltip title={action.disabledReason} placement="top" variant="solid">
                                                            <Box>{button}</Box>
                                                        </Tooltip>
                                                    ) : button}
                                                    <Typography level="body-sm" color="neutral">
                                                        {action.description}
                                                    </Typography>
                                                    {action.disabledReason && (
                                                        <Typography level="body-xs" color="warning">
                                                            {action.disabledReason}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </TabPanel>
                        </Tabs>
                    </form>
                </DrawerWithStickyFooter>
            </Drawer>
            <React.Fragment>
                <ConfirmDeleteModal key={bookingId + ' - ' - openDeleteModal}
                                    discardMessage={"Are you sure you want to delete this booking?"}
                                    openState={openDeleteModal} parentCallback={handleDeleteCallBack}/>
            </React.Fragment>
            <React.Fragment>
                <ConfirmSendKlarnaInvoice key={bookingId + ' - ' - openDeleteModal} booking_id={bookingId}
                                          openState={openSendKlarnaInvoice}
                                          parentCallback={handleSendKlaranaInvoiceCallback}/>
            </React.Fragment>
            
            {/* Cancellation Reason Modal */}
            <Modal open={cancelModalOpen} onClose={handleCancelModalClose}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ minWidth: 400 }}>
                    <DialogTitle>
                        <WarningRoundedIcon />
                        Cancel Booking
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography level="body-md">
                            Please provide a reason for cancelling this booking.
                        </Typography>
                        
                        <Box>
                            <Typography level="body-sm" sx={{ mb: 0.5 }}>Cancellation Reason</Typography>
                            <Select
                                placeholder="Select a reason..."
                                value={cancellationReason}
                                onChange={(_event, newValue) => setCancellationReason(newValue)}
                                sx={{ width: '100%' }}
                            >
                                {CANCELLATION_REASONS.map((reason) => (
                                    <Option key={reason} value={reason}>{reason}</Option>
                                ))}
                            </Select>
                        </Box>

                        <Box>
                            <Typography level="body-sm" sx={{ mb: 0.5 }}>Additional Details</Typography>
                            <Textarea
                                placeholder="Please provide more details about the cancellation..."
                                value={cancellationDetails}
                                onChange={(e) => setCancellationDetails(e.target.value)}
                                minRows={3}
                                maxRows={5}
                                sx={{ width: '100%' }}
                            />
                        </Box>

                        {(!cancellationReason || !cancellationDetails.trim()) && (
                            <Typography level="body-sm" color="warning" startDecorator={<WarningAmber sx={{ fontSize: 16 }} />}>
                                Both reason and details are required to cancel.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleConfirmCancellation}
                            disabled={!isCancellationValid}
                        >
                            Confirm Cancellation
                        </Button>
                        <Button variant="plain" color="neutral" onClick={handleCancelModalClose}>
                            Go Back
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
            <ConfirmMoveBookingModal
                open={openMoveConfirmModal}
                bookingTitle={customer?.full_name || 'this booking'}
                newDate={booking?.booked_in && !isNaN(new Date(String(booking.booked_in)).getTime()) ? new Date(String(booking.booked_in)).toLocaleDateString('en-GB') : 'selected date'}
                onConfirm={handleConfirmMoveOnSave}
                onCancel={handleCancelMoveOnSave}
                isSubmitting={isSaving}
            />
        </>
    );
}